import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Radio, 
  Shield, 
  FileText, 
  Siren,
  CheckCircle,
  ArrowRight,
  Mail,
  Cpu,
  Camera,
  MapPin,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  {
    id: 'locating',
    icon: Radio,
    title: 'Utility Locating & Marking',
    description: 'Comprehensive locating for gas, electric, water, sewer, fiber, and telecom infrastructure using state-of-the-art electromagnetic and GPR technology.',
    features: [
      'Comprehensive locating for gas, electric, water, sewer, fiber, and telecom',
      'State-of-the-art electromagnetic & GPR technology',
      'Compliance with 811, state regulations, and client-specific standards',
      'Color-coded marking per APWA standards',
      'Detailed documentation and reporting'
    ],
    color: 'text-skytec-primary',
    bgColor: 'bg-skytec-primary-dim'
  },
  {
    id: 'damage-prevention',
    icon: Shield,
    title: 'Damage Prevention Programs',
    description: 'Proactive risk identification and excavation support to prevent utility strikes and ensure regulatory compliance.',
    features: [
      'Proactive risk identification',
      'Excavation support and on-site safety oversight',
      'Root-cause analysis and corrective action planning',
      'Safety training and compliance documentation',
      'Incident investigation and reporting'
    ],
    color: 'text-skytec-secondary',
    bgColor: 'bg-skytec-secondary-dim'
  },
  {
    id: 'field-intelligence',
    icon: FileText,
    title: 'Field Intelligence & Reporting',
    description: 'Real-time digital documentation with AI-supported quality checks for accurate, verifiable field data.',
    features: [
      'Real-time digital documentation',
      'Photo-verified markouts',
      'GPS-enabled mapping and asset verification',
      'AI-supported quality checks',
      'Automated reporting and analytics'
    ],
    color: 'text-skytec-accent',
    bgColor: 'bg-skytec-accent/20'
  },
  {
    id: 'emergency',
    icon: Siren,
    title: 'Emergency Response',
    description: '24/7 rapid deployment teams for storm, outage, and restoration support serving critical infrastructure clients.',
    features: [
      'Rapid deployment teams',
      'Storm, outage, and restoration support',
      '24/7 availability for critical infrastructure clients',
      'Priority scheduling for urgent projects',
      'Disaster recovery coordination'
    ],
    color: 'text-red-500',
    bgColor: 'bg-red-500/10'
  }
];

const technologies = [
  {
    icon: Radio,
    title: 'Electromagnetic Locating',
    description: 'Industry-standard equipment for detecting metallic utilities with precision.'
  },
  {
    icon: Cpu,
    title: 'Ground-Penetrating Radar',
    description: 'Advanced GPR technology for non-metallic utilities and subsurface imaging.'
  },
  {
    icon: Camera,
    title: 'Photo Documentation',
    description: 'Visual verification of all markings with GPS-tagged photography.'
  },
  {
    icon: MapPin,
    title: 'GIS Mapping',
    description: 'Accurate digital mapping and asset location data for client records.'
  }
];

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Core Services</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              OUR <span className="neon-text">SERVICES</span>
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg">
              USG delivers high-accuracy utility locating, field intelligence, and safety-driven operations 
              that reduce risk, prevent outages, and accelerate project delivery.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-skytec-surface rounded-2xl border border-skytec-border p-8 lg:p-12"
                data-testid={`service-${service.id}`}
              >
                <div className="grid lg:grid-cols-2 gap-8 items-start">
                  <div>
                    <div className={`w-16 h-16 ${service.bgColor} rounded-xl flex items-center justify-center mb-6`}>
                      <service.icon className={`w-8 h-8 ${service.color}`} />
                    </div>
                    <h2 className="font-heading text-2xl lg:text-3xl font-bold text-skytec-fg mb-4 uppercase">
                      {service.title}
                    </h2>
                    <p className="text-skytec-fg/70 text-lg mb-6">
                      {service.description}
                    </p>
                    <Link to="/contact">
                      <Button 
                        className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading"
                      >
                        REQUEST THIS SERVICE
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                  <div className="bg-skytec-bg rounded-xl border border-skytec-border p-6">
                    <h3 className="font-heading font-bold text-skytec-fg mb-4 uppercase">Key Features</h3>
                    <ul className="space-y-3">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-skytec-accent mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-skytec-fg/70">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="technology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Technology</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              ADVANCED EQUIPMENT
            </h2>
            <p className="text-skytec-fg/60 mt-4 max-w-2xl mx-auto">
              We combine 130+ experienced utility locators with advanced locating equipment and AI-enabled field technology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-skytec-bg rounded-xl border border-skytec-border"
              >
                <div className="w-14 h-14 bg-skytec-primary-dim rounded-full flex items-center justify-center mx-auto mb-4">
                  <tech.icon className="w-7 h-7 text-skytec-primary" />
                </div>
                <h3 className="font-heading font-bold text-skytec-fg mb-2">{tech.title}</h3>
                <p className="text-skytec-fg/60 text-sm">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              READY TO GET STARTED?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Contact us today to discuss your utility locating needs. Our experienced team 
              is ready to support your project with precision and safety.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold"
                  data-testid="services-contact-btn"
                >
                  CONTACT US FOR CONSULTATION
                </Button>
              </Link>
              <a href="mailto:info@usgllc811.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  EMAIL US
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
